import InspirationBoard from '@/components/ProjectDetail/InspirationBoard';
import KeyResults from '@/components/ProjectDetail/KeyResults';
import { ActionRelation } from '@/constants/action';
import { CATEGORIES_ICONS } from '@/constants/category';
import { useCategoryManager } from '@/services/categories/hooks';
import { useProjectDetail } from '@/services/project/hooks';
import {
  IconClock,
  IconCommitPersonOutline,
  IconComplete,
  IconFire,
  IconIncompleteDark,
  IconLeveragePersonOutline,
  IconProgress,
  IconRecurrence,
  IconStar,
  IconStarFull,
  IconTarget,
} from '@/theme/icons';
import { Action } from '@/types/actions';
import {
  calculateTotalDuration,
  getActionsByBlockId,
  getCompletedActions,
  getIncompleteActions,
  getTotalDurationOfActionsExtendedFormat,
} from '@/utils/action';
import { utcToLocalDate } from '@/utils/calendar';
import { getColorFromToken } from '@/utils/color';
import { getTotalDurationOfStarredActions, validateRecurrence } from '@/utils/events';
import { fixUncategorizedName, getSecondsFromDuration, humanDuration } from '@/utils/index';
import rem from '@/utils/rem';
import { Box, Flex, HStack, Icon, Text, VStack } from '@chakra-ui/react';
import { format } from 'date-fns';
import { orderBy } from 'lodash';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

const darkColor = 'var(--chakra-colors-blue-1000)';
const lightColor = 'var(--chakra-colors-gray-50)';
const grayColor = '#B1BBC833';
const borderColor = '#B1BBC8';

export type PrintableProjectProps = {
  categoryId: string;
  projectId: string;
};

type ActionItemProps = {
  action: Action;
  categoryColor: string;
};

type BlockProps = {
  categoryId: string;
  blockId: string;
};

const ActionItemPrintable = ({ action, categoryColor }: ActionItemProps) => {
  const { t } = useTranslation('ProjectDetailComponents');

  const completeIcon = {
    incomplete: <IconIncompleteDark width={rem(12)} height={rem(12)} />,
    'in-progress': (
      <Icon
        as={IconProgress}
        sx={{ path: { '&:first-of-type': { fill: 'currentColor' } }, '--progress-background': 'white' }}
        width={rem(12)}
        height={rem(12)}
      />
    ),
    complete: <Icon as={IconComplete} sx={{ path: { stroke: lightColor } }} width={rem(12)} height={rem(12)} />,
  };

  const scheduledDateWithTime =
    action.scheduledDate &&
    utcToLocalDate(action.scheduledDate, action.scheduledTime, action.timezone, action.gmtOffset);
  const date = scheduledDateWithTime && format(scheduledDateWithTime, 'MM/dd/yy');
  const time = action.scheduledTime && scheduledDateWithTime && format(scheduledDateWithTime, 'hh:mm a');

  return (
    <Flex
      sx={{ breakInside: 'avoid' }}
      gap={rem(8)}
      padding={`${rem(6)} ${rem(8)} ${rem(8)} ${rem(8)}`}
      bgGradient={
        action.dateOfStarring
          ? `linear-gradient(270deg, ${getColorFromToken(categoryColor, '0.20')} 0%, white 60%)`
          : undefined
      }
      border={`${rem(1)} solid ${darkColor}`}
      borderRadius={rem(4)}
    >
      <VStack paddingY={rem(6)}>{action.progressStatus && completeIcon[action.progressStatus]}</VStack>
      <Box width="full">
        <HStack alignItems="flex-start">
          <Text textStyle="xsmall" paddingY={rem(6)}>
            {action.title}
          </Text>

          <HStack flexShrink={0} marginLeft="auto" paddingY={rem(6)}>
            {action?.promises?.[0] && (
              <HStack
                gap={rem(4)}
                border={`${rem(1)} solid ${borderColor}`}
                borderRadius={rem(4)}
                backgroundColor={lightColor}
                marginY={rem(-6)}
                paddingX={rem(6)}
                paddingY={rem(4)}
              >
                {action?.promises[0]?.kind === ActionRelation.Leverage && (
                  <Icon as={IconLeveragePersonOutline} width={rem(14)} height={rem(14)} />
                )}
                {action?.promises[0]?.kind === ActionRelation.Commit && (
                  <Icon as={IconCommitPersonOutline} width={rem(14)} height={rem(14)} />
                )}
                <Text textStyle="xsmall">{action?.promises[0]?.person.name}</Text>
              </HStack>
            )}

            {validateRecurrence(action.event?.recurrence) && (
              <Icon as={IconRecurrence} width={rem(12)} height={rem(12)} />
            )}

            {date && (
              <Text
                as="div"
                sx={{
                  span: {
                    fontWeight: '900',
                  },
                }}
                textStyle="xsmall"
                border={`${rem(1)} solid ${borderColor}`}
                borderRadius={rem(4)}
                backgroundColor={lightColor}
                marginY={rem(-6)}
                paddingX={rem(6)}
                paddingY={rem(4)}
              >
                <span>{date}</span> {time}
              </Text>
            )}
            <Icon as={IconClock} width={rem(12)} height={rem(12)} />
            <Text textStyle="xsmall">{humanDuration(getSecondsFromDuration(action.duration))}</Text>

            <Icon
              as={action.dateOfStarring ? IconStarFull : IconStar}
              width={rem(14)}
              height={rem(14)}
              color={action.dateOfStarring ? categoryColor : 'currentcolor'}
            />
          </HStack>
        </HStack>
        {action.notes && (
          <Box
            width="full"
            marginTop={rem(2)}
            padding={rem(8)}
            border={`${rem(1)} solid ${getColorFromToken(categoryColor, '1')}`}
            borderRadius={rem(4)}
            backgroundColor={lightColor}
          >
            <Text textStyle="xsmallBlack">{t('printable.actionNotes')}</Text>
            <Text textStyle="xsmall">{action.notes}</Text>
          </Box>
        )}
      </Box>
    </Flex>
  );
};

const BlockItemPrintable = ({ categoryId, blockId }: BlockProps) => {
  const { t } = useTranslation('ProjectDetailComponents');

  const { data: CMdata } = useCategoryManager({ categoryId });

  const category = CMdata?.category;
  const block = CMdata?.blocks.find((block) => block.id === blockId);

  const blockActions = useMemo(() => {
    if (!blockId) return;
    const actions = getActionsByBlockId(blockId, CMdata?.actions);

    return actions;
  }, [CMdata?.actions, blockId]);

  if (!category || !block) {
    return <></>;
  }

  const incompleteActions = getIncompleteActions(blockActions ?? [], 'blockOrder');
  const completedActions = getCompletedActions(blockActions ?? [], 'modifiedAt');

  const totalDuration = humanDuration(calculateTotalDuration(incompleteActions) * 60);
  const starredActionsTotalDuration = getTotalDurationOfStarredActions(incompleteActions);

  return (
    <Box width="full" color={darkColor} backgroundColor={lightColor}>
      <Flex alignItems="stretch" gap={rem(16)} padding={rem(24)} backgroundColor={grayColor}>
        <Flex
          flex="1"
          gap={rem(8)}
          minWidth={0}
          padding={rem(16)}
          paddingLeft={rem(8)}
          borderRadius={rem(4)}
          backgroundColor={lightColor}
        >
          <Icon as={IconTarget} width={rem(14)} height={rem(14)} />

          <Flex flexDirection="column" gap={rem(8)}>
            <Text textStyle="smallBlack">{t('printable.result')}</Text>
            <Text textStyle="xsmall">{block.result}</Text>
          </Flex>
        </Flex>
        <Flex
          flex="1"
          gap={rem(8)}
          minWidth={0}
          padding={rem(16)}
          paddingLeft={rem(8)}
          color={darkColor}
          borderRadius={rem(4)}
          backgroundColor={lightColor}
        >
          <Icon as={IconFire} width={rem(14)} height={rem(14)} />

          <Flex flexDirection="column" gap={rem(8)}>
            <Text textStyle="smallBlack">{t('printable.purpose')}</Text>
            <Text textStyle="xsmall">{block.purpose}</Text>
          </Flex>
        </Flex>
      </Flex>
      <Box padding={rem(24)}>
        <Flex flexDirection="column" gap={rem(8)}>
          <Text textStyle="smallBlack">{t('printable.massiveActionPlan')}</Text>

          <Flex alignItems="stretch" gap={rem(8)} marginY={rem(8)}>
            <Flex
              alignItems="center"
              flex="1"
              gap={rem(12)}
              minWidth={0}
              padding={rem(20)}
              bgGradient={`linear-gradient(270deg, ${getColorFromToken(category.color, '0.10')} 0%, ${grayColor} 66.97%)`}
              borderRadius={rem(4)}
            >
              <Icon as={IconStarFull} width={rem(14)} height={rem(14)} color={darkColor} />
              <Text textStyle="small">{t('printable.starredTime')}</Text>
              <Text textStyle="smallBlack" marginLeft="auto">
                {starredActionsTotalDuration || '0h'}
              </Text>
            </Flex>

            <Flex
              alignItems="center"
              flex="1"
              gap={rem(12)}
              minWidth={0}
              padding={rem(20)}
              borderRadius={rem(4)}
              backgroundColor={grayColor}
            >
              <Icon as={IconClock} width={rem(14)} height={rem(14)} opacity="0.7" />
              <Text textStyle="small">{t('printable.totalTime')}</Text>
              <Text textStyle="smallBlack" marginLeft="auto">
                {totalDuration || '0h'}
              </Text>
            </Flex>
          </Flex>

          {incompleteActions.map((action) => (
            <ActionItemPrintable action={action} categoryColor={category.color} key={action.id} />
          ))}

          {completedActions.map((action) => (
            <ActionItemPrintable action={action} categoryColor={category.color} key={action.id} />
          ))}
        </Flex>
      </Box>
    </Box>
  );
};

function PrintableProject({ categoryId, projectId }: PrintableProjectProps) {
  const { t } = useTranslation('ProjectDetailComponents');

  const { data: CMdata } = useCategoryManager({
    categoryId,
  });

  const { data: project } = useProjectDetail(projectId);

  const ultimateResult = project?.projectByPk?.result;
  const ultimatePurpose = project?.projectByPk?.purpose;
  const projectName = project?.projectByPk?.name;

  const category = CMdata?.category;

  const filteredActions = useMemo(() => {
    if (!project?.projectByPk) {
      return [];
    }

    let captureList = project.projectByPk.actions;

    // Filter actions without a blockId, but do not sort them here
    return orderBy(
      captureList.filter((action) => action.blockId === null),
      'projectOrder',
      'asc',
    );
  }, [project?.projectByPk]);

  const starredDuration = useMemo(
    () => getTotalDurationOfStarredActions(project?.projectByPk.actions || []),
    [project?.projectByPk.actions],
  );

  const allActionsDuration = useMemo(
    () => getTotalDurationOfActionsExtendedFormat(project?.projectByPk.actions || []),
    [project?.projectByPk.actions],
  );

  if (!category || !project) {
    return <></>;
  }

  const incompleteActions = getIncompleteActions(filteredActions ?? [], 'projectOrder');
  const completedActions = getCompletedActions(filteredActions ?? [], 'modifiedAt');

  return (
    <Box width="full" color={darkColor} backgroundColor={lightColor}>
      <HStack justifyContent="space-between" paddingX={rem(24)} paddingY={rem(16)}>
        <Text textStyle="mediumBlack">{projectName}</Text>
        <HStack>
          <Flex
            alignItems="center"
            justifyContent="center"
            width={rem(18)}
            height={rem(18)}
            marginRight={rem(4)}
            borderRadius="full"
            aspectRatio={1}
            backgroundColor={category.color}
          >
            <Icon as={CATEGORIES_ICONS?.[category.icon ?? 'uncategorized']} width={rem(10)} height={rem(10)} />
          </Flex>
          <Text textStyle="mediumBlack">{fixUncategorizedName(category.name)}</Text>
        </HStack>
      </HStack>
      <Flex alignItems="stretch" gap={rem(16)} padding={rem(10)} backgroundColor={lightColor}>
        <Flex
          flex="1"
          gap={rem(12)}
          minWidth={0}
          padding={rem(16)}
          paddingLeft={rem(8)}
          bgGradient={`linear-gradient(270deg, ${getColorFromToken(category.color, '0.10')} 0%, ${grayColor} 66.97%)`}
          borderRadius={rem(4)}
        >
          <Icon as={IconStarFull} width={rem(14)} height={rem(14)} color={darkColor} />
          <Text textStyle="small">{t('printable.starredActions')}</Text>
          <Text textStyle="smallBlack" marginLeft="auto">
            {starredDuration || '0h'}
          </Text>
        </Flex>
        <Flex
          flex="1"
          gap={rem(12)}
          minWidth={0}
          padding={rem(16)}
          paddingLeft={rem(8)}
          color={darkColor}
          borderRadius={rem(4)}
          backgroundColor={grayColor}
        >
          <Icon as={IconClock} width={rem(14)} height={rem(14)} opacity="0.7" />
          <Text textStyle="small">{t('printable.allActions')}</Text>
          <Text textStyle="smallBlack" marginLeft="auto">
            {allActionsDuration || '0h'}
          </Text>
        </Flex>
      </Flex>
      <Flex alignItems="stretch" gap={rem(16)} padding={rem(10)} backgroundColor={grayColor}>
        <Flex
          flex="1"
          gap={rem(8)}
          minWidth={0}
          padding={rem(16)}
          paddingLeft={rem(8)}
          borderRadius={rem(4)}
          backgroundColor={lightColor}
        >
          <Icon as={IconTarget} width={rem(14)} height={rem(14)} />

          <Flex flexDirection="column" gap={rem(8)}>
            <Text textStyle="smallBlack">{t('printable.ultimateResult')}</Text>
            <Text textStyle="xsmall">{ultimateResult}</Text>
          </Flex>
        </Flex>
        <Flex
          flex="1"
          gap={rem(8)}
          minWidth={0}
          padding={rem(16)}
          paddingLeft={rem(8)}
          color={darkColor}
          borderRadius={rem(4)}
          backgroundColor={lightColor}
        >
          <Icon as={IconFire} width={rem(14)} height={rem(14)} />

          <Flex flexDirection="column" gap={rem(8)}>
            <Text textStyle="smallBlack">{t('printable.ultimatePurpose')}</Text>
            <Text textStyle="xsmall">{ultimatePurpose}</Text>
          </Flex>
        </Flex>
      </Flex>
      {project.projectByPk.key_results?.length > 0 && (
        <Flex alignItems="stretch" gap={rem(16)} padding={rem(10)} backgroundColor={grayColor}>
          <Flex
            flex="1"
            gap={rem(8)}
            minWidth={0}
            padding={rem(16)}
            paddingLeft={rem(8)}
            borderRadius={rem(4)}
            backgroundColor={lightColor}
          >
            <KeyResults
              color={category?.color ?? ''}
              gradientColor={category?.color ?? ''}
              keyResults={project.projectByPk.key_results}
              projectId={project.projectByPk.id}
              isArchived={true}
              onCreateAction={(name: string) => name}
              onCreateBlock={(name: string) => name}
            />
          </Flex>
        </Flex>
      )}

      {project?.projectByPk?.inspiration_board_items?.length > 0 && (
        <Flex alignItems="stretch" gap={rem(16)} padding={rem(10)} backgroundColor={grayColor}>
          <Flex
            flex="1"
            gap={rem(8)}
            minWidth={0}
            padding={rem(16)}
            paddingLeft={rem(8)}
            borderRadius={rem(4)}
            backgroundColor={lightColor}
          >
            <InspirationBoard
              color={category?.color ?? ''}
              data={project?.projectByPk?.inspiration_board_items}
              onOpenImagesModal={() => {}}
              onDeleteInspirationBoardItem={() => {}}
              onOpenImageViewer={() => {}}
              onEditNoteModal={() => {}}
              onCreateNoteModal={() => {}}
              isArchived={true}
              isFullColorMode={true}
            />
          </Flex>
        </Flex>
      )}

      {(incompleteActions.length > 0 || incompleteActions.length > 0) && (
        <Flex alignItems="stretch" gap={rem(16)} padding={rem(10)} backgroundColor={grayColor}>
          <Flex
            flex="1"
            gap={rem(8)}
            minWidth={0}
            padding={rem(16)}
            paddingLeft={rem(8)}
            borderRadius={rem(4)}
            backgroundColor={lightColor}
          >
            <Flex flexDirection="column" gap={rem(8)} width="full">
              <Text textStyle="mediumBlack">{t('printable.captureList')}</Text>
              {incompleteActions.map((action) => (
                <ActionItemPrintable action={action} categoryColor={category.color} key={action.id} />
              ))}

              {completedActions.map((action) => (
                <ActionItemPrintable action={action} categoryColor={category.color} key={action.id} />
              ))}
            </Flex>
          </Flex>
        </Flex>
      )}

      {project?.projectByPk?.blocks?.length > 0 && (
        <Flex alignItems="stretch" gap={rem(16)} padding={rem(10)} backgroundColor={grayColor}>
          <Flex
            flex="1"
            gap={rem(8)}
            minWidth={0}
            padding={rem(16)}
            paddingLeft={rem(8)}
            borderRadius={rem(4)}
            backgroundColor={lightColor}
          >
            <Flex flexDirection="column" gap={rem(8)} width="full">
              <Text textStyle="mediumBlack">{t('printable.rpmBlocks')}</Text>

              {project?.projectByPk?.blocks.map((block) => (
                <Box
                  key={'box_' + block.id}
                  sx={{ breakInside: 'avoid' }}
                  gap={rem(8)}
                  padding={`${rem(6)} ${rem(8)} ${rem(8)} ${rem(8)}`}
                  border={`${rem(1)} solid ${darkColor}`}
                  borderRadius={rem(4)}
                >
                  <BlockItemPrintable key={block.id} blockId={block.id} categoryId={block.categoryId} />
                </Box>
              ))}
            </Flex>
          </Flex>
        </Flex>
      )}
    </Box>
  );
}

export default PrintableProject;
